import {forwardRef, type ReactNode, type Ref, useId, useState} from 'react';
import {cn} from '../../lib/cn';
import type {SelectOption} from '../../lib/selector-option';
import {
  LegacySelectContent,
  LegacySelectItem,
  LegacySelectRoot,
  LegacySelectTrigger,
  LegacySelectValue,
  type LegacySelectSize,
} from './components';

export type LegacySelectProps<
  D = unknown,
  V extends string | number = string,
  L extends ReactNode = string,
> = {
  placeholder?: string;
  label?: string | ReactNode;
  className?: string;
  size?: LegacySelectSize;
  options?: SelectOption<D, V, L>[];
  children?: ReactNode;
  value?: string;
  onChange?: (value: V) => void;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  supportingText?: string;
  errorText?: string;
  containerClassName?: string;
  triggerClassName?: string;
};

const LegacySelectInner = <
  V extends string | number = string,
  D = unknown,
  L extends ReactNode = string,
>(
  {
    placeholder,
    label,
    className,
    size = 'sm',
    options,
    supportingText,
    errorText,
    containerClassName,
    triggerClassName,
    children,
    value,
    onChange,
    onValueChange,
    disabled,
  }: LegacySelectProps<D, V, L>,
  ref: Ref<HTMLButtonElement>,
) => {
  const id = useId();
  const [isOpen, setIsOpen] = useState(false);
  const hasValue = Boolean(value);
  const showLabel = hasValue || isOpen;

  const handleValueChange = (newValue: string) => {
    onChange?.(newValue as V);
    onValueChange?.(newValue);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  return (
    <div className={cn('group relative w-[300px]', className, containerClassName)}>
      {label && showLabel && (
        <label
          htmlFor={id}
          className="absolute start-1 top-0 z-10 block -translate-y-1/2 bg-white px-2 text-xs font-medium text-[var(--chekin-color-brand-navy)] group-has-[:disabled]:opacity-50"
        >
          {label}
        </label>
      )}
      <LegacySelectRoot
        value={value}
        onValueChange={handleValueChange}
        onOpenChange={handleOpenChange}
        disabled={disabled}
      >
        <LegacySelectTrigger id={id} ref={ref} size={size} className={triggerClassName}>
          <LegacySelectValue placeholder={placeholder} />
        </LegacySelectTrigger>
        <LegacySelectContent>
          {children ??
            options?.map(option => (
              <LegacySelectItem
                key={String(option.value)}
                value={String(option.value)}
                size={size}
                disabled={option.isDisabled}
              >
                {option.label}
              </LegacySelectItem>
            ))}
        </LegacySelectContent>
      </LegacySelectRoot>
      {Boolean(supportingText || errorText) && (
        <div className="flex min-h-[15px] justify-between pt-1">
          {supportingText && !errorText && (
            <span className="text-xs italic leading-[15px] text-[var(--chekin-color-gray-1)]">
              {supportingText}
            </span>
          )}
          {errorText && (
            <span className="ml-auto text-right text-xs font-medium leading-4 text-[var(--error-message-color)]">
              {errorText}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const LegacySelectForward = forwardRef(LegacySelectInner);
LegacySelectForward.displayName = 'LegacySelect';

export const LegacySelect = LegacySelectForward as <
  V extends string | number = string,
  D = unknown,
  L extends ReactNode = string,
>(
  props: LegacySelectProps<D, V, L> & {
    ref?: Ref<HTMLButtonElement>;
  },
) => ReturnType<typeof LegacySelectInner>;
